import { DESIGN_AREA_PROPERTY, DESIGN_ANSWER_VERSION, designAreasOf, type Scene } from '@pz/scene';
import type { ProductDefinition, Values } from './types.js';

/**
 * The product picture a design area is edited on — Zazzle's shirt behind the
 * artboard — worked out from the template's own mockup.
 *
 * A design question fills one zone on the print side and a COPY of that zone
 * on each mockup; the copy's transform is where the owner placed the design on
 * the product photograph. That rectangle, as fractions of the mockup, is what
 * the designer needs to put the photograph under its zone. The picture itself
 * is the mockup rendered with every OTHER answer the shopper gave (a shirt
 * colour chosen through an option shows) and this zone left empty, so the
 * designer's own live layers are the only copy of the design on screen.
 *
 * Only a flat, upright copy qualifies. A zone in perspective (a mug's curve) or
 * turned on the photograph cannot be matched by an upright artboard, and a
 * backdrop that is almost right is worse than a plain one.
 */
export interface MockupZone {
  side: Scene;
  /** The zone copy on that mockup, as fractions of the mockup's size. */
  fraction: { x: number; y: number; w: number; h: number };
}

export function mockupZoneOf(
  product: Pick<ProductDefinition, 'sides' | 'bindings'>,
  optionId: string,
  preferredSideId?: string | null,
): MockupZone | undefined {
  const wanted = new Set(
    (product.bindings ?? [])
      .filter((binding) => binding.optionId === optionId && binding.property === DESIGN_AREA_PROPERTY)
      .map((binding) => binding.elementId),
  );
  if (wanted.size === 0) return undefined;
  const mockups = (product.sides ?? []).filter((side) => side.target === 'preview');
  // The mockup the shopper is looking at first, then the rest in order.
  mockups.sort((a, b) => Number(b.id === preferredSideId) - Number(a.id === preferredSideId));
  for (const side of mockups) {
    if (!(side.width > 0) || !(side.height > 0)) continue;
    for (const zone of designAreasOf(side)) {
      if (!wanted.has(zone.id)) continue;
      const t = zone.transform;
      if (!t || zone.perspective) continue;
      const turn = (((t.rotation ?? 0) % 360) + 360) % 360;
      if (Math.min(turn, 360 - turn) > 0.5) continue;
      if (t.skewX || t.skewY) continue;
      const fraction = {
        x: (t.cx - t.w / 2) / side.width,
        y: (t.cy - t.h / 2) / side.height,
        w: t.w / side.width,
        h: t.h / side.height,
      };
      if (fraction.w <= 0 || fraction.h <= 0 || fraction.x < -1e-6 || fraction.y < -1e-6
        || fraction.x + fraction.w > 1 + 1e-6 || fraction.y + fraction.h > 1 + 1e-6) continue;
      return { side, fraction };
    }
  }
  return undefined;
}

/**
 * The answers the backdrop is drawn from: everything the shopper chose, with
 * THIS zone explicitly empty. Absent would not do — an unanswered zone draws
 * its authored starting design, which would then show twice.
 */
export function backdropValues(values: Values, optionId: string): Values {
  return { ...values, [optionId]: { v: DESIGN_ANSWER_VERSION, layers: [] } };
}
